import { useEffect, useState } from 'react';
import {
  IonActionSheet,
  IonAlert,
  IonButton,
  IonIcon,
  IonItem,
  IonItemOption,
  IonItemOptions,
  IonItemSliding,
  IonLabel,
  IonList,
  IonNote,
  IonSpinner,
} from '@ionic/react';
import {
  carSportOutline,
  createOutline,
  keypadOutline,
  linkOutline,
  personAddOutline,
  qrCodeOutline,
  trashOutline,
} from 'ionicons/icons';
import { Card, Page } from '../components/Shell';
import { connections as connectionsApi } from '../lib/connections';
import { connectLink, shareText } from '../lib/share';
import { getDeviceName } from '../lib/device';
import type { Connection } from '../lib/types';

interface Props {
  onBack: () => void;
  onStartRide: (contact: Connection) => void;
  /** Ein von Hand eingetippter Einladungscode - fuehrt zum Bestaetigen-Screen. */
  onEnterCode: (code: string) => void;
  onError: (message: string) => void;
  onNotice: (message: string) => void;
}

export function Contacts({ onBack, onStartRide, onEnterCode, onError, onNotice }: Props) {
  const [items, setItems] = useState<Connection[] | null>(null);
  const [inviting, setInviting] = useState(false);
  const [inviteChoice, setInviteChoice] = useState<string | null>(null);
  const [showingCode, setShowingCode] = useState<string | null>(null);
  const [enteringCode, setEnteringCode] = useState(false);
  const [renaming, setRenaming] = useState<Connection | null>(null);
  const [removing, setRemoving] = useState<Connection | null>(null);

  useEffect(() => {
    void load();
  }, []);

  async function load() {
    try {
      const { connections: list } = await connectionsApi.list();
      setItems(Array.isArray(list) ? list : []);
    } catch (error) {
      onError(error instanceof Error ? error.message : 'Kontakte konnten nicht geladen werden.');
      setItems([]);
    }
  }

  async function invite() {
    if (!getDeviceName().trim()) {
      onError('Bitte lege in den Einstellungen zuerst deinen Namen fest - der Kontakt sieht ihn.');
      return;
    }
    setInviting(true);
    try {
      const { code } = await connectionsApi.invite();
      setInviteChoice(code);
    } catch (error) {
      onError(error instanceof Error ? error.message : 'Einladung konnte nicht erzeugt werden.');
    } finally {
      setInviting(false);
    }
  }

  async function shareInviteLink(code: string) {
    // Nicht window.location.origin: in der Android-App waere das
    // https://localhost - ein Link, mit dem der Freund nichts anfangen kann.
    const notice = await shareText({
      title: 'R2F - Kontakt verbinden',
      text: `Lass uns in R2F verbinden. Mit der App: Kontakte -> "Hast du selbst einen Code bekommen?" -> ${code}. Sonst dieser Link:`,
      url: connectLink(code),
    });
    if (notice) onNotice(notice);
  }

  async function copyCode(code: string) {
    try {
      await navigator.clipboard.writeText(code);
      onNotice('Code kopiert.');
    } catch {
      // Kein Zwischenspeicher erreichbar - der Code steht ja schon auf dem Bildschirm.
    }
  }

  function submitCode(rawCode: string) {
    const code = rawCode.trim().toUpperCase();
    if (!code) return;
    // Nicht ueber die Adresszeile: in der Android-App wuerde die WebView
    // damit auf die Server-Webseite springen - raus aus der App, ohne Weg
    // zurueck. Der Bestaetigen-Screen ist derselbe wie beim Link, er wird
    // hier nur direkt aufgerufen.
    onEnterCode(code);
  }

  async function confirmRename(label: string) {
    if (!renaming) return;
    try {
      const { connection } = await connectionsApi.rename(renaming.id, label);
      if (connection) {
        setItems((prev) => prev?.map((c) => (c.id === connection.id ? connection : c)) ?? prev);
      }
    } catch (error) {
      onError(error instanceof Error ? error.message : 'Umbenennen fehlgeschlagen.');
    }
  }

  async function confirmRemove() {
    if (!removing) return;
    const id = removing.id;
    try {
      await connectionsApi.remove(id);
      setItems((prev) => prev?.filter((c) => c.id !== id) ?? prev);
    } catch (error) {
      onError(error instanceof Error ? error.message : 'Trennen fehlgeschlagen.');
    }
  }

  return (
    <Page title="Meine Kontakte" onBack={onBack}>
      <Card>
        <h2>Einmal verbinden, immer griffbereit</h2>
        <p>
          Lade einen Freund einmalig ein. Danach reicht ein Tipp, um mit ihm eine Fahrt zu
          starten - er bekommt sofort eine Benachrichtigung, kein Code nötig.
        </p>
        <IonButton expand="block" size="large" style={{ marginTop: 12 }} onClick={invite} disabled={inviting}>
          {inviting ? (
            <IonSpinner name="dots" />
          ) : (
            <>
              <IonIcon slot="start" icon={personAddOutline} />
              Kontakt verbinden
            </>
          )}
        </IonButton>
        <IonButton expand="block" fill="clear" size="small" onClick={() => setEnteringCode(true)}>
          <IonIcon slot="start" icon={keypadOutline} />
          Hast du selbst einen Code bekommen?
        </IonButton>
      </Card>

      {items === null ? (
        <div className="r2f-row" style={{ justifyContent: 'center', padding: 24 }}>
          <IonSpinner name="dots" />
        </div>
      ) : items.length === 0 ? (
        <Card>
          <h2>Noch keine Kontakte</h2>
          <p>Verbinde dich einmal mit jemandem - danach steht er hier zur Schnellauswahl.</p>
        </Card>
      ) : (
        <Card flush>
          <IonList lines="full">
            {items.map((contact) => (
              <IonItemSliding key={contact.id}>
                <IonItem button onClick={() => onStartRide(contact)}>
                  <IonIcon slot="start" icon={carSportOutline} color="primary" />
                  <IonLabel>
                    <h3>{contact.label}</h3>
                    <IonNote>Fahrt jetzt starten</IonNote>
                  </IonLabel>
                </IonItem>
                <IonItemOptions side="end">
                  <IonItemOption onClick={() => setRenaming(contact)}>
                    <IonIcon slot="icon-only" icon={createOutline} />
                  </IonItemOption>
                  <IonItemOption color="danger" onClick={() => setRemoving(contact)}>
                    <IonIcon slot="icon-only" icon={trashOutline} />
                  </IonItemOption>
                </IonItemOptions>
              </IonItemSliding>
            ))}
          </IonList>
        </Card>
      )}

      <p className="r2f-muted">
        Eine Verbindung erlaubt es dem Kontakt, dir auch ohne aktive Fahrt eine Benachrichtigung
        zu schicken. Du kannst sie jederzeit trennen.
      </p>

      <IonAlert
        isOpen={renaming !== null}
        header="Kontakt umbenennen"
        inputs={[
          { name: 'label', type: 'text', value: renaming?.label ?? '', attributes: { maxlength: 40 } },
        ]}
        buttons={[
          { text: 'Abbrechen', role: 'cancel' },
          {
            text: 'Speichern',
            handler: (data: { label?: string }) => {
              if (data.label?.trim()) void confirmRename(data.label);
            },
          },
        ]}
        onDidDismiss={() => setRenaming(null)}
      />

      <IonAlert
        isOpen={removing !== null}
        header="Verbindung trennen?"
        message={removing ? `"${removing.label}" wird entfernt - bei beiden Seiten.` : ''}
        buttons={[
          { text: 'Behalten', role: 'cancel' },
          { text: 'Trennen', role: 'destructive', handler: () => void confirmRemove() },
        ]}
        onDidDismiss={() => setRemoving(null)}
      />

      {/* Nach dem Erzeugen des Codes: Link teilen oder nur den Code zeigen -
          der Link stolpert manchmal in der iOS-Web-App, der Code geht immer. */}
      <IonActionSheet
        isOpen={inviteChoice !== null}
        header="Wie soll dein Kontakt die Einladung bekommen?"
        onDidDismiss={() => setInviteChoice(null)}
        buttons={[
          {
            text: 'Link teilen',
            icon: linkOutline,
            handler: () => {
              if (inviteChoice) void shareInviteLink(inviteChoice);
            },
          },
          {
            text: 'Code anzeigen',
            icon: qrCodeOutline,
            handler: () => setShowingCode(inviteChoice),
          },
          { text: 'Abbrechen', role: 'cancel' },
        ]}
      />

      <IonAlert
        isOpen={showingCode !== null}
        header="Einladungscode"
        subHeader={showingCode ?? ''}
        message='Gilt 24 Stunden. Dein Kontakt gibt ihn unter "Hast du selbst einen Code bekommen?" ein.'
        buttons={[
          { text: 'Fertig', role: 'cancel' },
          {
            text: 'Kopieren',
            handler: () => {
              if (showingCode) void copyCode(showingCode);
              return false;
            },
          },
        ]}
        onDidDismiss={() => setShowingCode(null)}
      />

      <IonAlert
        isOpen={enteringCode}
        header="Code eingeben"
        message="Den Code bekommst du von deinem Kontakt, wenn der Link bei ihm nicht funktioniert."
        inputs={[{ name: 'code', type: 'text', placeholder: 'ABC123', attributes: { maxlength: 8 } }]}
        buttons={[
          { text: 'Abbrechen', role: 'cancel' },
          {
            text: 'Weiter',
            handler: (data: { code?: string }) => {
              if (data.code?.trim()) submitCode(data.code);
            },
          },
        ]}
        onDidDismiss={() => setEnteringCode(false)}
      />
    </Page>
  );
}
